// utils/collectionFamily.js
//
// SUB-COLLECTIONS (keepr-api docs/SCHEMA.md § Sub-collections; keepr-web
// docs/plans/sub-collections.md §5): how a client reads a collection's
// family off what the API already sends — never a request of its own.
//
//   - `GET /api/collections/{id}` adds `hierarchy` to a collection in a
//     family: `parent`, `ancestors` (root first), `subCollections` (the ones
//     the caller can read, each with its own `cardIds`, `itemCount` and the
//     caller's `myAccess` there), `effectiveCardIds` (own + inherited),
//     `familyCardIds` (+ every sub-collection's own), `inheritedCardIds`.
//     ABSENT for a top-level collection with none — so every helper here
//     answers "no family" against an API a deploy behind.
//   - `GET /api/collections` rows carry `parent: { _id, name, icon, color }`
//     beside `parent_id`.
//   - An ancestor the caller may not open (their access is on the
//     sub-collection alone) is a summary with `readable: false`: its name and
//     colors, never a link.
//
// One level deep in v1, but nothing here assumes it: chains are arrays.

import { normalizeId } from './ids';

const summaryOf = raw => {
	if (!raw || typeof raw !== 'object') return null;
	const id = normalizeId(raw._id || raw.id);
	if (!id) return null;
	return {
		_id: id,
		name: typeof raw.name === 'string' && raw.name.trim() ? raw.name : 'Untitled collection',
		icon: raw.icon || null,
		color: raw.color || null,
		// A list row's `parent` says nothing about readability; only
		// `hierarchy` does. Absent means "assume openable" — a link the
		// server answers with its own 404 at worst.
		readable: raw.readable !== false,
	};
};

/** The collection's `hierarchy` object, or null (a top-level collection with no family). */
export const hierarchyOf = collection => {
	const hierarchy = collection?.hierarchy;
	return hierarchy && typeof hierarchy === 'object' ? hierarchy : null;
};

/** The parent's summary `{ _id, name, icon, color, readable }`, from `hierarchy.parent` or a list row's `parent`. */
export function parentOf(collection) {
	return summaryOf(hierarchyOf(collection)?.parent) || summaryOf(collection?.parent);
}

/** Is this collection a sub-collection (it names a parent)? */
export const isSubCollection = collection => Boolean(normalizeId(collection?.parent_id) || parentOf(collection));

/** The ancestor chain, root first (`[parent]` at depth 1). */
export function ancestorsOf(collection) {
	const listed = hierarchyOf(collection)?.ancestors;
	if (Array.isArray(listed) && listed.length) return listed.map(summaryOf).filter(Boolean);
	const parent = parentOf(collection);
	return parent ? [parent] : [];
}

/**
 * The sub-collections the caller can read, live ones first in the API's
 * order: `{ _id, name, icon, color, status, cardIds: Set, itemCount, myAccess }`.
 */
export function subCollectionsOf(collection) {
	const listed = hierarchyOf(collection)?.subCollections;
	if (!Array.isArray(listed)) return [];
	return listed.map(raw => {
		const summary = summaryOf(raw);
		if (!summary) return null;
		return {
			...summary,
			readable: true,
			status: raw.status || 'active',
			cardIds: new Set((Array.isArray(raw.cardIds) ? raw.cardIds : []).map(normalizeId).filter(Boolean)),
			itemCount: typeof raw.itemCount === 'number' ? raw.itemCount : null,
			myAccess: raw.myAccess || null,
		};
	}).filter(Boolean);
}

export const hasSubCollections = collection => subCollectionsOf(collection).length > 0;

const idSet = list => new Set((Array.isArray(list) ? list : []).map(normalizeId).filter(Boolean));

/** The cards a sub-collection inherits from above (`hierarchy.inheritedCardIds`). */
export const inheritedCardIdsOf = collection => idSet(hierarchyOf(collection)?.inheritedCardIds);

/**
 * The cards the collection's LIST can hold beyond its own: at a parent, its
 * sub-collections' own cards (`familyCardIds` less the effective set). Empty
 * for a collection with no sub-collections.
 */
export function descendantCardIdsOf(collection) {
	const hierarchy = hierarchyOf(collection);
	if (!hierarchy) return new Set();
	const effective = idSet(hierarchy.effectiveCardIds);
	const family = idSet(hierarchy.familyCardIds);
	subCollectionsOf(collection).forEach(sub => sub.cardIds.forEach(id => family.add(id)));
	return new Set([...family].filter(id => !effective.has(id)));
}

/**
 * The breadcrumb segments for the ancestors, root first: a readable one
 * links to its list, one the caller cannot open is a plain segment.
 */
export const ancestorCrumbs = collection => ancestorsOf(collection).map(ancestor => ({
	label: ancestor.name,
	to: ancestor.readable ? `/collections/${ancestor._id}/items` : undefined,
}));

/**
 * The sub-collection an item of this collection's family list lives in —
 * its summary, or null when the item lives in the collection itself (or in
 * no member this client knows of, which then says nothing rather than
 * guessing).
 */
export function familyMemberOf(item, collection) {
	const itemCollectionId = normalizeId(item?.collection_id);
	const collectionId = normalizeId(collection?._id);
	if (!itemCollectionId || !collectionId || itemCollectionId === collectionId) return null;
	return subCollectionsOf(collection).find(sub => sub._id === itemCollectionId) || null;
}

/** The sub-collection that owns `cardId`, as a summary — null when the card is usable in the collection itself. */
export function cardHomeOf(cardId, collection) {
	const id = normalizeId(cardId);
	if (!id) return null;
	// A card usable here is at home here, whatever a sub-collection's refs
	// say (a local override ref to a card owned above is not ownership).
	if (idSet(hierarchyOf(collection)?.effectiveCardIds).has(id)) return null;
	return subCollectionsOf(collection).find(sub => sub.cardIds.has(id)) || null;
}

/**
 * A collections list in reading order: each sub-collection immediately after
 * its parent when the parent is in the same list; one whose parent is not
 * here (the caller cannot see it, or it sits in another section) keeps its
 * place. Stable otherwise.
 */
export function orderWithSubCollections(collections = []) {
	return nestSubCollections(collections).map(entry => entry.collection);
}

/**
 * The same reading order, each row with its `depth`: 0 for a collection
 * whose parent is not in the list, 1 beneath a listed parent, and so on down
 * a chain. What a picker indents by (`nestedOptionClass`). A cycle (data no
 * API should send) breaks at the first collection seen again, which then
 * stands at the top level rather than vanishing.
 */
export function nestSubCollections(collections = []) {
	const list = Array.isArray(collections) ? collections : [];
	const present = new Set(list.map(c => normalizeId(c?._id)).filter(Boolean));
	const childrenOf = new Map();
	const tucked = new Set();
	list.forEach(c => {
		const parentId = normalizeId(c?.parent_id) || parentOf(c)?._id;
		const id = normalizeId(c?._id);
		if (!parentId || !id || parentId === id || !present.has(parentId)) return;
		if (!childrenOf.has(parentId)) childrenOf.set(parentId, []);
		childrenOf.get(parentId).push(c);
		tucked.add(id);
	});
	if (!tucked.size) return list.map(collection => ({ collection, depth: 0 }));
	const out = [];
	const placed = new Set();
	const place = (c, depth) => {
		const id = normalizeId(c?._id);
		if (id && placed.has(id)) return;
		if (id) placed.add(id);
		out.push({ collection: c, depth });
		(childrenOf.get(id) || []).forEach(child => place(child, depth + 1));
	};
	list.forEach(c => { if (!tucked.has(normalizeId(c?._id))) place(c, 0); });
	// Only a cycle leaves rows unplaced: none of its members is a root.
	list.forEach(c => { if (!placed.has(normalizeId(c?._id))) place(c, 0); });
	return out;
}

/** The option class a picker row carries at `depth` — none at the top level. */
export const nestedOptionClass = depth => (depth > 0
	? `collection-option--nested collection-option--depth-${Math.min(depth, 3)}`
	: undefined);

/** parentId → how many of `collections` name it as their parent. */
export function subCollectionCountsIn(collections = []) {
	const counts = new Map();
	(Array.isArray(collections) ? collections : []).forEach(c => {
		const parentId = normalizeId(c?.parent_id) || parentOf(c)?._id;
		if (parentId) counts.set(parentId, (counts.get(parentId) || 0) + 1);
	});
	return counts;
}

/** The phrase a pin, a Recent row or a palette entry carries for a sub-collection: its parent's name. */
export const parentKicker = collection => parentOf(collection)?.name || null;

/**
 * Where an item of `cardId` is made when it is launched from `collection`:
 * the sub-collection that owns the card (a parent's shortcut for a
 * sub-collection's card makes the record THERE — keepr-api § Sub-collections
 * "Definitions on a parent may name a sub-collection's card"), or null when
 * the card is usable in `collection` itself.
 */
export function launchHomeFor(collection, cardId) {
	return cardHomeOf(cardId, collection);
}

/**
 * What a permission check for `cardId` reads, launched from `collection`:
 * the collection itself, or — for a sub-collection's card — that
 * sub-collection's access summary (`hierarchy.subCollections[].myAccess`),
 * so a card the caller cannot write where it lives is never offered.
 */
export function accessContextFor(collection, cardId) {
	const home = launchHomeFor(collection, cardId);
	if (!home) return collection;
	return { _id: home._id, name: home.name, myAccess: home.myAccess || {} };
}

/** The ancestor of `collection` whose id is `id` (its summary, `readable` included), or null. */
export function ancestorById(collection, id) {
	const target = normalizeId(id);
	if (!target) return null;
	return ancestorsOf(collection).find(ancestor => ancestor._id === target) || null;
}

/** Every collection id of `collection`'s family this client knows of: itself, its ancestors, its readable sub-collections. */
export function familyIdsOf(collection) {
	const ids = new Set();
	const self = normalizeId(collection?._id);
	if (self) ids.add(self);
	ancestorsOf(collection).forEach(ancestor => ids.add(ancestor._id));
	subCollectionsOf(collection).forEach(sub => ids.add(sub._id));
	return ids;
}

/**
 * The path that opens a record living in `collection`'s ancestor `recordCollectionId`
 * — a parent record a sub-collection's item points at (keepr-api § Sub-collections,
 * the referenced-record read). A reader of the parent opens it there; one whose
 * access is on the sub-collection alone cannot open the parent's routes, so it
 * opens in THIS collection's list, in its read-only pane (`?item=`). Null when
 * the record does not live in an ancestor.
 */
export function referencedRecordPath(collection, recordCollectionId, itemId) {
	const ancestor = ancestorById(collection, recordCollectionId);
	const id = normalizeId(itemId);
	if (!ancestor || !id) return null;
	if (ancestor.readable) return `/collections/${ancestor._id}/items/${id}`;
	return `/collections/${normalizeId(collection?._id)}/items?item=${encodeURIComponent(id)}`;
}

/**
 * The cards usable in `collection` that LOOK UP a card owned by one of its
 * ancestors — `[{ card, element, target, ancestor }]`, one per element. A
 * reader here opens the ancestor's records these items already point at (the
 * referenced-record read) but cannot choose other ones without access there,
 * which the Sharing tab says (docs/plans/sub-collections.md §5.7).
 * `cardIds` is the effective set; `getElementsForCard` the definitions
 * context's resolver (inherited and element-set elements included).
 */
export function lookupsAbove(collection, cardIds, cardsById = {}, getElementsForCard) {
	const ancestors = new Map(ancestorsOf(collection).map(ancestor => [ancestor._id, ancestor]));
	if (!ancestors.size || typeof getElementsForCard !== 'function') return [];
	const out = [];
	[...(cardIds || [])].forEach(rawId => {
		const cardId = normalizeId(rawId);
		const card = cardsById[cardId];
		if (!card) return;
		(getElementsForCard(cardId) || []).forEach(element => {
			if (element?.dataType !== 'card-lookup') return;
			const target = cardsById[normalizeId(element?.options?.lookupCardId)];
			const ancestor = target ? ancestors.get(normalizeId(target.collection_id)) : null;
			if (ancestor) out.push({ card, element, target, ancestor });
		});
	});
	return out;
}

/**
 * May the caller change things ON `collection`'s parent (its sharing, its
 * rules)? Only when they manage it there: the collection's owner (the
 * parent's owner by construction), or a manager whose access here comes from
 * the parent. A manager of the sub-collection alone reads the parent's rows
 * but is never offered a door to change them.
 */
export function managesParentOf(collection) {
	const parent = parentOf(collection);
	if (!parent || !parent.readable) return false;
	const access = collection?.myAccess || {};
	if (access.isOwner || access.isAdmin) return true;
	return access.role === 'manage' && normalizeId(access.inheritedFrom) === parent._id;
}
